import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const QUERY_PATTERNS = [
  {
    name: 'Time Series',
    pattern: 'Show me [metric] over [time period]',
    examples: [
      'Show me revenue over the last 6 months',
      'Display user signups over time by month'
    ],
    icon: '📈'
  },
  {
    name: 'Top N',
    pattern: 'What are the top [N] [items] by [metric]?',
    examples: [
      'What are the top 10 products by sales?',
      'Show me the top 5 regions by revenue'
    ],
    icon: '🏆'
  },
  {
    name: 'Comparison',
    pattern: 'Compare [metric] across [dimension]',
    examples: [
      'Compare sales across different regions',
      'Compare this month vs last month revenue'
    ],
    icon: '⚖️'
  },
  {
    name: 'Breakdown',
    pattern: 'Break down [metric] by [dimension]',
    examples: [
      'Break down revenue by product category',
      'Show sales by customer segment'
    ],
    icon: '🥧'
  },
  {
    name: 'Aggregation',
    pattern: 'What is the [aggregation] [metric]?',
    examples: [
      'What is the total revenue this year?',
      'What is the average order value?'
    ],
    icon: '🎯'
  },
  {
    name: 'Filtering',
    pattern: 'Show [metric] for [condition]',
    examples: [
      'Show revenue for orders over $1000',
      'Display customers from California'
    ],
    icon: '🔍'
  }
];

export default function QueryPatterns({ onPatternClick, isOpen, onToggle }) {
  const copyPattern = (pattern) => {
    navigator.clipboard.writeText(pattern);
    toast.success('Pattern copied!');
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed right-6 bottom-24 z-10 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-full p-3 shadow-lg hover:shadow-xl transition-all hover:scale-105"
        title="Show query patterns"
      >
        <span className="text-2xl">💡</span>
      </button>
    );
  }

  return (
    <div className="fixed right-6 bottom-24 z-10 w-96 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-2xl overflow-hidden">
      <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">💡</span>
          <h3 className="font-semibold text-white">Query Patterns</h3>
        </div>
        <button
          onClick={onToggle}
          className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto p-4 space-y-4">
        {QUERY_PATTERNS.map((pattern, idx) => (
          <div
            key={idx}
            className="bg-surface-50 dark:bg-surface-900 rounded-lg p-4 border border-surface-200 dark:border-surface-700"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">{pattern.icon}</span>
                <h4 className="font-semibold text-surface-900 dark:text-white">
                  {pattern.name}
                </h4>
              </div>
              <button
                onClick={() => copyPattern(pattern.pattern)}
                className="text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200 transition-colors p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-800"
                title="Copy pattern"
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-3">
              <code className="text-sm font-mono bg-white dark:bg-surface-800 px-2 py-1 rounded text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                {pattern.pattern}
              </code>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-surface-500 dark:text-surface-400 uppercase">
                Examples:
              </p>
              {pattern.examples.map((example, exIdx) => (
                <button
                  key={exIdx}
                  onClick={() => onPatternClick(example)}
                  className="w-full text-left text-sm text-surface-700 dark:text-surface-300 hover:text-primary-600 dark:hover:text-primary-400 italic bg-white dark:bg-surface-800 px-3 py-2 rounded border border-surface-200 dark:border-surface-700 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
                >
                  "{example}"
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 bg-surface-50 dark:bg-surface-900 border-t border-surface-200 dark:border-surface-700 px-4 py-3">
        <p className="text-xs text-surface-600 dark:text-surface-400 text-center">
          Click any example to use it or copy the pattern
        </p>
      </div>
    </div>
  );
}
